import { ArrowLeftRight, ArrowRight } from "lucide-react";
import { AUTO, type Language } from "@/lib/languages.ts";
import type { Direction } from "@/lib/protocol.ts";

type Props = {
  languages: Language[];
  direction: Direction;
  source: string;
  target: string;
  secondTarget: string;
  allowSecond: boolean;
  disabled: boolean;
  onChange: (next: { direction?: Direction; source?: string; target?: string; secondTarget?: string }) => void;
};

function LangSelect({
  id,
  label,
  value,
  languages,
  allowAuto,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  languages: Language[];
  allowAuto?: boolean;
  disabled: boolean;
  onChange: (code: string) => void;
}) {
  const tested = languages.filter((l) => l.tested);
  const others = languages.filter((l) => !l.tested);
  return (
    <label htmlFor={id} className="flex min-w-0 flex-1 flex-col gap-1 text-xs text-muted">
      {label}
      <select
        id={id}
        className="field w-full min-w-0 text-base text-fg"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      >
        {allowAuto ? <option value={AUTO}>Auto-detect</option> : null}
        <optgroup label="Tested">
          {tested.map((l) => (
            <option key={l.code} value={l.code} title={l.native}>
              {l.name}
            </option>
          ))}
        </optgroup>
        <optgroup label="Untested">
          {others.map((l) => (
            <option key={l.code} value={l.code} title={l.native}>
              {l.name}
            </option>
          ))}
        </optgroup>
      </select>
    </label>
  );
}

export function LanguageBar({ languages, direction, source, target, secondTarget, allowSecond, disabled, onChange }: Props) {
  const twoWay = direction === "two-way";
  const swap = () => {
    if (source === AUTO) return;
    onChange({ source: target, target: source });
  };
  return (
    <div className="flex flex-col gap-3">
      <div role="radiogroup" aria-label="Translation mode" className="inline-flex w-full rounded-xl border border-line bg-surface-2 p-1 sm:w-auto">
        {(["one-way", "two-way"] as const).map((d) => (
          <button
            key={d}
            type="button"
            role="radio"
            aria-checked={direction === d}
            disabled={disabled}
            onClick={() => onChange({ direction: d, ...(d === "two-way" && source === AUTO ? { source: target === "fr" ? "en" : "fr" } : {}) })}
            className={`min-h-9 flex-1 rounded-lg px-4 text-sm transition-colors sm:flex-none ${
              direction === d ? "bg-fg font-medium text-bg shadow-sm" : "text-muted hover:text-fg"
            }`}
          >
            {d === "one-way" ? "One-way" : "Conversation"}
          </button>
        ))}
      </div>
      <div className="flex items-end gap-2">
        <LangSelect
          id="lang-a"
          label={twoWay ? "Person A speaks" : "From"}
          value={source}
          languages={languages}
          allowAuto={!twoWay}
          disabled={disabled}
          onChange={(code) => onChange({ source: code })}
        />
        <button
          type="button"
          className="btn mb-0 h-11 w-11 shrink-0 px-0"
          onClick={swap}
          disabled={disabled || source === AUTO}
          aria-label="Swap languages"
          title="Swap languages"
        >
          {twoWay ? <ArrowLeftRight size={18} /> : <ArrowRight size={18} />}
        </button>
        <LangSelect
          id="lang-b"
          label={twoWay ? "Person B speaks" : "To"}
          value={target}
          languages={languages}
          disabled={disabled}
          onChange={(code) => onChange({ target: code })}
        />
      </div>
      {!twoWay && allowSecond ? (
        <label htmlFor="lang-c" className="flex items-center gap-2 text-xs text-muted">
          <span className="shrink-0">2nd attendee language</span>
          <select
            id="lang-c"
            className="field min-h-9 min-w-0 flex-1 text-sm text-fg"
            value={secondTarget}
            disabled={disabled}
            onChange={(e) => onChange({ secondTarget: e.target.value })}
          >
            <option value="">None (faster)</option>
            {languages
              .filter((l) => l.code !== source && l.code !== target)
              .map((l) => (
                <option key={l.code} value={l.code}>
                  {l.name}
                </option>
              ))}
          </select>
        </label>
      ) : null}
    </div>
  );
}
